import { DatePipe } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Observable, takeUntil } from "rxjs";
import { BaseComponent } from "src/app/base.component";
import { Constants } from "src/app/shared/data/constants";
import {
  FilterInterface,
  paymentLPFilter,
} from "src/app/shared/data/filter-data";
import { Authorities } from "src/app/shared/enum/authorities";
import { PartnerStatus } from "src/app/shared/enum/partner-status";
import { PaymentFrequency } from "src/app/shared/enum/payment-frequency";
import {
  PaymentStatus,
  PaymentStatusToShow,
} from "src/app/shared/enum/paymentStatus";
import { RefreshButtonState } from "src/app/shared/enum/refreshButtonState";
import {
  RemunerationEnum,
  RemunerationEnumMapping,
} from "src/app/shared/enum/renumeration.enum";
import { UserHistoryActionsEnum } from "src/app/shared/enum/userHistoryActions";
import { UserHistoryServicesEnum } from "src/app/shared/enum/userHistoryService";
import { AdvertisingCampaignRemunerationDetails } from "src/app/shared/models/advertising-campaigns/advertisingCampaignRemunerationDetails.interface";
import { FilterParams } from "src/app/shared/models/filterParams";
import { FranchisesRemunerationWithResultDto } from "src/app/shared/models/franchise/franchisesRemunerationWithResultDto.interface";
import { PaymentDetails } from "src/app/shared/models/franchise/paymentDetails.interface";
import { RemunerationDetailsResponse } from "src/app/shared/models/franchise/remunerationDetailsResponse.interface";
import { RemunerationFilterDto } from "src/app/shared/models/franchise/remunerationFilterDto.interface";
import { IColumn } from "src/app/shared/models/table/column.interface";
import { BadgeService } from "src/app/shared/services/badge.service";
import { FiltersService } from "src/app/shared/services/filters.service";
import { SaveUserHistoryService } from "src/app/shared/services/history/saveHistory";
import { JwtService } from "src/app/shared/services/jwt.service";
import { AppStateInterface } from "src/app/store/appState.interface";
import {
  invokePaymentDetails,
  resultPaymentDetails,
} from "src/app/store/franchise/franchise.action";
import { resultPaymentDetailsSelector } from "src/app/store/franchise/franchise.selectors";
import { invokeRemunerationDetails } from "src/app/store/locationPartner/location-partner.action";
import {
  resultLoadingRemunerationSelector,
  resultRemunerationDetailsSelector,
} from "src/app/store/locationPartner/location-partner.selectors";
import { UtilFunctions } from "src/app/utils/utilFunctions";

@Component({
  selector: "app-remuneration",
  templateUrl: "./remuneration.component.html",
  styleUrls: ["./remuneration.component.scss"],
})
export class RemunerationComponent extends BaseComponent implements OnInit {
  columns: IColumn[] = [
    {
      key: "companyName",
      label: "admin.locations.repository.locations",
      sorter: false,
    },
    {
      key: "price",
      label: "admin.locations.payment.price",
      sorter: false,
    },
    {
      key: "paymentFrequency",
      label: "admin.locations.payment.paymentFrequency",
      sorter: false,
    },
    {
      key: "campaigns",
      label: "franchise.remuneration.campaigns.campaigns",
      sorter: false,
    },
    {
      key: "startDate",
      label: "franchise.remuneration.campaigns.startDate",
      sorter: false,
    },
    {
      key: "endDate",
      label: "franchise.remuneration.campaigns.endDate",
      sorter: false,
    },
    {
      key: "impression",
      label: "franchise.remuneration.campaigns.impressions",
      sorter: false,
    },
    {
      key: "remuneration",
      label: "franchise.remuneration.campaigns.remuneration",
      sorter: false,
    },
    {
      key: "status",
      label: "franchise.remuneration.campaigns.status",
      sorter: false,
    },
    {
      key: "totalTTC",
      label: "admin.locations.payment.totalTTC",
      sorter: false,
    },
  ];

  formFilter: FormGroup;
  filterPayment: FilterInterface = paymentLPFilter;

  isPaid: boolean | null;

  forbidden: boolean | null = false;

  remunerationDetails$: Observable<FranchisesRemunerationWithResultDto | null>;
  remunerationDetails: RemunerationDetailsResponse[] = [];
  count: number = 0;

  remunerationFilter: RemunerationFilterDto = {
    companyName: "",
    endDate: null,
    isPaid: null,
    pager: {
      pageNumber: 1,
      pageSize: Constants.defaultTableSize,
    },
    startDate: null,
  };
  loading: boolean = true;
  paymentFrequency = PaymentFrequency;

  remunerationEnum = RemunerationEnum;
  loading$: Observable<boolean>;

  paymentDetails: PaymentDetails = {
    nextPayment: 0,
    totalFinishedCampaigns: 0,
    totalFranchised: 0,
    totalViews: 0,
  };

  paymentDetails$: Observable<PaymentDetails | null>;

  loadingPayment: boolean = true;

  pageSize: number = Constants.defaultTableSize;
  pageNumber: number = 1;

  filters: FilterParams | null = null;

  cachedAt: Date | null;
  fetchingHistoryState: RefreshButtonState = RefreshButtonState.IDLE;
  remunerationEnumMapping = RemunerationEnumMapping;

  constructor(
    public badgeService: BadgeService,
    private store: Store<AppStateInterface>,
    private fb: FormBuilder,
    private filtersService: FiltersService,
    private jwtService: JwtService,
    private saveHistory: SaveUserHistoryService,
    private translate: TranslateService,
    private datePipe: DatePipe
  ) {
    super(store);
    this.formFilter = this.fb.group({
      companyName: [""],
      date: [[null, null]],
    });
    this.remunerationDetails$ = this.store
      .pipe(select(resultRemunerationDetailsSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.loading$ = this.store
      .pipe(select(resultLoadingRemunerationSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.paymentDetails$ = this.store
      .pipe(select(resultPaymentDetailsSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    const authorities = this.jwtService.getPermissions(jwtService.getToken());
    if (authorities) {
      this.forbidden = !authorities.includes(
        Authorities.LP_PAYMENT_GETALL.toString()
      );
    }

    this.extractFilters();
  }

  extractFilters() {
    this.filters = this.filtersService.decodeUri();
    if (this.filters) {
      this.remunerationFilter.isPaid =
        this.findOption(this.filters.status) ?? null;
    }
    this.pageNumber = this.filtersService.extractValueFromUrl(
      this.filters,
      "page",
      this.pageNumber
    );
    this.pageSize = this.filtersService.extractValueFromUrl(
      this.filters,
      "size",
      this.pageSize
    );

    this.filtersService.patchFormValue(
      this.filters,
      ["companyName", "date"],
      this.formFilter
    );

    this.remunerationFilter = {
      ...this.remunerationFilter,
      companyName: this.formFilter.get("companyName")?.value,
      startDate: this.formFilter.get("date")?.value[0],
      endDate: this.formFilter.get("date")?.value[1],
    };
  }

  ngOnInit(): void {
    this.filterPayment.name.map(
      (e) =>
        (e.data = Object.keys(PaymentStatus)
          .filter((value) => isNaN(Number(value)) === true)
          .map(
            (key: string | any) =>
              PaymentStatusToShow[key as keyof typeof PaymentStatus]
          ))
    );
    this.filterData(false);
    this.store.dispatch(resultPaymentDetails({ paymentDetails: null }));
    this.store.dispatch(invokePaymentDetails());
    this.remunerationDetails$.subscribe((result) => {
      if (result) {
        this.cachedAt = result.cachedAt;
        this.remunerationDetails = result.remunerationDetails;
        this.count = result.count;
      }
    });
    this.loading$.subscribe((result) => {
      this.loading = result;
    });
    this.paymentDetails$.subscribe((result) => {
      if (result) {
        this.loadingPayment = false;
        this.paymentDetails = result;
      }
    });

    this.formFilter.valueChanges.subscribe((value) => {
      this.pageNumber = 1;
      this.pageSize = Constants.defaultTableSize;
      this.remunerationFilter = {
        ...this.remunerationFilter,
        companyName: value.companyName,
        startDate: value.date[0] ?? null,
        endDate: value.date[1] ?? null,
      };
      this.filterData(false);
    });

    this.saveHistory.saveUserHistory(
      UserHistoryActionsEnum.GET,
      UserHistoryServicesEnum.REMUNERATION_LP
    );
  }

  saveOptionFilter(option: any | null) {
    this.isPaid = this.findOption(option);
    this.remunerationFilter = {
      ...this.remunerationFilter,
      isPaid: this.isPaid,
    };
    this.filterData(false);
  }

  findOption(option: any | null) {
    const KeyFound = Object.keys(PaymentStatusToShow).find(
      (key) => PaymentStatusToShow[key as PaymentStatus] === option
    );
    switch (KeyFound) {
      case PaymentStatus.PAID:
        this.isPaid = true;
        break;
      case PaymentStatus.UNPAID:
        this.isPaid = false;
        break;
      default:
        this.isPaid = null;
    }
    return this.isPaid;
  }

  filterData(refreshCache: boolean) {
    if (refreshCache) {
      this.loading$.subscribe((isLoading) => {
        if (isLoading) {
          this.fetchingHistoryState = RefreshButtonState.LOADING;
        } else {
          this.fetchingHistoryState = RefreshButtonState.SUCCESS;
          setTimeout(
            () => (this.fetchingHistoryState = RefreshButtonState.IDLE),
            2500
          );
        }
      });
      this.loadingPayment = true;
    }
    this.store.dispatch(
      invokeRemunerationDetails({
        filter: {
          ...this.remunerationFilter,
          pager: {
            pageNumber: this.pageNumber,
            pageSize: this.pageSize,
          },
        },
        refreshCache: refreshCache,
      })
    );

    this.store.dispatch(invokePaymentDetails());
  }

  changePage(event: number) {
    this.pageNumber = event;
    this.filterData(false);
  }

  itemsPerPageChange(pageSize: number) {
    this.pageSize = pageSize;
    this.pageNumber = 1;
    this.filterData(false);
  }

  showTooltip(remunerationStatus: RemunerationEnum): boolean {
    return UtilFunctions.showTooltip(remunerationStatus);
  }

  getRevenueTooltip(
    paidImpressions: number,
    paidPeriodStart: Date,
    paidPeriodEnd: Date
  ): string {
    return UtilFunctions.getRevenueTooltip(
      paidImpressions,
      paidPeriodStart,
      paidPeriodEnd,
      this.translate,
      this.datePipe
    );
  }
}
